import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import type {
    CreateDripSequence as CreateDripSequenceMutation,
    CreateDripSequenceVariables as CreateDripSequenceMutationVariables,
    DuplicateDripSequence as DuplicateDripSequenceMutation,
    DuplicateDripSequenceVariables as DuplicateDripSequenceMutationVariables,
    DeleteDripSequence as DeleteDripSequenceMutation,
    DeleteDripSequenceVariables as DeleteDripSequenceMutationVariables,
    UpdateDripSequence as UpdateDripSequenceMutation,
    UpdateDripSequenceVariables as UpdateDripSequenceMutationVariables,
    RestoreDripSequence as RestoreDripSequenceMutation,
    RestoreDripSequenceVariables as RestoreDripSequenceMutationVariables,
    GetDripSequences as GetDripSequencesQuery,
    GetDripSequencesVariables as GetDripSequencesQueryVariables,
    CampaignUpdateInput,
} from 'nutshell-graphql-types';

import CREATE_DRIP_SEQUENCE from './graphql/mutations/create-drip-sequence.graphql';
import DELETE_DRIP_SEQUENCE from './graphql/mutations/delete-drip-sequence.graphql';
import DUPLICATE_DRIP_SEQUENCE from './graphql/mutations/duplicate-drip-sequence.graphql';
import RESTORE_DRIP_SEQUENCE from './graphql/mutations/restore-drip-sequence.graphql';
import UPDATE_DRIP_SEQUENCE from './graphql/mutations/update-drip-sequence.graphql';
import GET_DRIP_SEQUENCES from './graphql/queries/get-drip-sequences.graphql';

type Props = {
    children: (arg1: {
        onCreate: (name: string, queryVariables?: GetDripSequencesQueryVariables) => Promise<any>;
        onDuplicate: (
            name: string,
            dripSequenceId: string,
            queryVariables?: GetDripSequencesQueryVariables
        ) => Promise<any>;
        onDelete: (
            dripSequenceId: string,
            queryVariables?: GetDripSequencesQueryVariables
        ) => Promise<any>;
        onRestore: (
            dripSequenceId: string,
            queryVariables?: GetDripSequencesQueryVariables
        ) => Promise<any>;
        onUpdate: (input: CampaignUpdateInput) => Promise<any>;
    }) => React.ReactElement;
};

export function DripSequenceMutations(props: Props) {
    const [createDripSequence] = useMutation<
        CreateDripSequenceMutation,
        CreateDripSequenceMutationVariables
    >(CREATE_DRIP_SEQUENCE);

    const [duplicateDripSequence] = useMutation<
        DuplicateDripSequenceMutation,
        DuplicateDripSequenceMutationVariables
    >(DUPLICATE_DRIP_SEQUENCE);

    const [deleteDripSequence] = useMutation<
        DeleteDripSequenceMutation,
        DeleteDripSequenceMutationVariables
    >(DELETE_DRIP_SEQUENCE);

    const [restoreDripSequence] = useMutation<
        RestoreDripSequenceMutation,
        RestoreDripSequenceMutationVariables
    >(RESTORE_DRIP_SEQUENCE);

    const [updateDripSequence] = useMutation<
        UpdateDripSequenceMutation,
        UpdateDripSequenceMutationVariables
    >(UPDATE_DRIP_SEQUENCE);

    return props.children({
        onCreate: (name: string, queryVariables?: GetDripSequencesQueryVariables) =>
            createDripSequence({
                variables: {dripSequence: {name: name}},
                update: (proxy, mutationResults) => {
                    const newDripSequence =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.dripSequenceCreate &&
                        mutationResults.data.dripSequenceCreate.dripSequence;

                    if (!newDripSequence) return;

                    // This will "filter" the new drip sequence from the list of
                    // archived drip sequences, but it's not in that list so its
                    // fine
                    dripSequenceDeleteRestoreUpdate(proxy, newDripSequence, false, queryVariables);
                },
            }).then(
                // Get the drip sequence for any additional promise chaining
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.dripSequenceCreate &&
                    mutationResults.data.dripSequenceCreate.dripSequence
            ),
        onDuplicate: (
            name: string,
            dripSequenceId: string,
            queryVariables?: GetDripSequencesQueryVariables
        ) =>
            duplicateDripSequence({
                variables: {input: {name: name, campaignId: dripSequenceId}},
                update: (proxy, mutationResults) => {
                    const newDripSequence =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.dripSequenceDuplicate &&
                        mutationResults.data.dripSequenceDuplicate.dripSequence;

                    if (!newDripSequence) return;

                    // This will "filter" the new drip sequence from the list of
                    // archived drip sequences, but it's not in that list so its
                    // fine
                    dripSequenceDeleteRestoreUpdate(proxy, newDripSequence, false, queryVariables);
                },
            }).then(
                // Get the drip sequence for any additional promise chaining
                (mutationResults) =>
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.dripSequenceDuplicate &&
                    mutationResults.data.dripSequenceDuplicate.dripSequence
            ),
        onRestore: (dripSequenceId: string, queryVariables?: GetDripSequencesQueryVariables) =>
            restoreDripSequence({
                variables: {id: dripSequenceId},
                update: (proxy, mutationResults) => {
                    // Get the restored dripSequence
                    const restoredDripSequence =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignRestore &&
                        mutationResults.data.campaignRestore.campaign;

                    if (!restoredDripSequence) return;

                    dripSequenceDeleteRestoreUpdate(
                        proxy,
                        restoredDripSequence,
                        false,
                        queryVariables
                    );
                },
            }),
        onDelete: (dripSequenceId: string, queryVariables?: GetDripSequencesQueryVariables) =>
            deleteDripSequence({
                variables: {id: dripSequenceId},
                update: (proxy, mutationResults) => {
                    // Get the archived dripSequence
                    const archivedDripSequence =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.campaignDelete &&
                        mutationResults.data.campaignDelete.campaign;

                    if (!archivedDripSequence) return;

                    dripSequenceDeleteRestoreUpdate(
                        proxy,
                        archivedDripSequence,
                        true,
                        queryVariables
                    );
                },
            }),
        onUpdate: (input: CampaignUpdateInput) =>
            updateDripSequence({
                variables: {dripSequence: input},
            }),
    });
}

/**
 * This will handle updating our local cache when we archive or restore a
 * drip sequence. It correctly handles updating both the archived and unarchived
 * caches.
 *
 * @param {DataProxy} proxy The mutation update proxy
 * @param {DripSequence} dripSequence The drip sequence that was mutated
 * @param {boolean} isDeleting True if we are deleting the given drip sequence,
 * false if we are restoring it.
 */
const dripSequenceDeleteRestoreUpdate = (
    // @ts-expect-error
    proxy,
    // @ts-expect-error
    dripSequence,
    isDeleting: boolean,
    listQueryVariables?: GetDripSequencesQueryVariables
) => {
    const queryVariables = listQueryVariables || {};

    try {
        // Update our archived cache
        // This isn't the best variable name, but its the cache we're removing
        // a drip sequence from.
        const cacheResultRemoving: GetDripSequencesQuery | null | undefined = proxy.readQuery({
            query: GET_DRIP_SEQUENCES,
            variables: {...queryVariables, archived: !isDeleting},
        });

        if (cacheResultRemoving && cacheResultRemoving.dripSequences) {
            proxy.writeQuery({
                query: GET_DRIP_SEQUENCES,
                variables: {...queryVariables, archived: !isDeleting},
                data: {
                    dripSequences: {
                        ...cacheResultRemoving.dripSequences,
                        edges: cacheResultRemoving.dripSequences.edges.filter((edge) => {
                            return edge.node.id !== dripSequence.id;
                        }),
                    },
                },
            });
        }
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }

    // Update our unarchived cache
    // Again, not the best name. We're adding a drip sequence to this cache
    try {
        const cacheResultAdding: GetDripSequencesQuery | null | undefined = proxy.readQuery({
            query: GET_DRIP_SEQUENCES,
            variables: {...queryVariables, archived: isDeleting},
        });

        if (!cacheResultAdding || !cacheResultAdding.dripSequences) {
            return;
        }

        proxy.writeQuery({
            query: GET_DRIP_SEQUENCES,
            variables: {...queryVariables, archived: isDeleting},
            data: {
                dripSequences: {
                    ...cacheResultAdding.dripSequences,
                    edges: [
                        {
                            cursor: dripSequence.id,
                            node: dripSequence,
                            __typename: 'DripSequencesEdge',
                        },
                    ].concat(cacheResultAdding.dripSequences.edges),
                },
            },
        });
    } catch {
        // If the above query doesn't exist in the cache, Apollo will throw an
        // error (why it can't just return null is beyond me). If that happens,
        // we need to "catch" the error. But we don't want to really do anything
        // with it. The just means we haven't performed this query yet. So, an
        // empty catch block
    }
};
